import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './SingleZone.scss';
import {
  fetchData,
  fetchSinglePipe,
  toggleZone,
  fetchSingleWaterSensor,
  fetchZoneWsAssociation
} from '../../APICalls/GetRequests';
import WaterSensorCard from "../WaterSensorCard/WaterSensorCard";
import { deleteZone } from '../../APICalls/DeleteRequests';
import Cookies from 'js-cookie';
import ZoneNamePopup from '../ZoneNamePopup/ZoneNamePopup';
import Spinner from '../Spinner/Spinner';
import '../PipesBar/PipesBar.scss';

const SingleZone = ({
  selectedField,
  setSelectedZone,
  setZones,
  selectedZone
}) => {
  const { zoneId: zoneIdFromParams } = useParams();
  const navigate = useNavigate();
  const sessionToken = Cookies.get('session_key');

  const zoneId =
    zoneIdFromParams ||
    selectedZone?.id ||
    Cookies.get('active_zone_id');

  const [zoneName, setZoneName] = useState('');
  const [zoneData, setZoneData] = useState(null);
  const [pipeData, setPipeData] = useState([]);
  const [zoneNameLoading, setZoneNameLoading] = useState(true);
  const [zonePipesLoading, setZonePipesLoading] = useState(true);
  const [zoneWaterSensors, setZoneWaterSensors] = useState([]);
  const [zoneWaterSensorsLoading, setZoneWaterSensorsLoading] = useState(true);
  const [zoneNamePopup, setZoneNamePopup] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!zoneId) {
      navigate(-1);
      return;
    }

    Cookies.set('active_zone_id', zoneId);
  }, [zoneId, navigate]);

  const refreshZonePipes = async () => {
    try {
      setZonePipesLoading(true);
      setZoneWaterSensorsLoading(true);

      // 1️⃣ Fetch zone core data
      const zoneRes = await fetchData('singleZone', sessionToken, zoneId);
      setZoneName(zoneRes.name);
      setZoneData(zoneRes);
      setSelectedZone(zoneRes);

      // 2️⃣ Fetch WS associations (NEW)
      const wsAssociation = await fetchZoneWsAssociation(
        sessionToken,
        zoneId
      );

      const wsIds = wsAssociation?.ws_ids || [];

      // 3️⃣ Fetch full sensor objects
      const waterSensorsRes = await Promise.all(
        wsIds.map((id) =>
          fetchSingleWaterSensor(sessionToken, id)
        )
      );

      setZoneWaterSensors(waterSensorsRes.filter(Boolean));

      // 4️⃣ Fetch pipes
      const uniquePipeIds = [...new Set(zoneRes.pipe_ids)];
      const zoneGateIds = zoneRes.gate_ids;

      const fetchedPipes = await Promise.all(
        uniquePipeIds.map(async (pipeId) => {
          const pipeInfo = await fetchSinglePipe(pipeId, sessionToken);
          if (!pipeInfo) return null;

          const updatedGates = pipeInfo.gates.map((gate) => ({
            ...gate,
            assigned: zoneGateIds.includes(gate.id)
          }));

          return { ...pipeInfo, gates: updatedGates };
        })
      );

      setPipeData(fetchedPipes.filter(Boolean));

    } catch (error) {
      console.error('Error refreshing zone or pipes:', error);
      setZoneWaterSensors([]);
    } finally {
      setZoneNameLoading(false);
      setZonePipesLoading(false);
      setZoneWaterSensorsLoading(false);
    }
  };

  useEffect(() => {
    if (zoneId) {
      refreshZonePipes();
    }
  }, [zoneId]);

  if (!zoneId || !zoneData) {
    return <Spinner />;
  }

  const allGatesClosed = pipeData.every((pipe) =>
    pipe.gates
      .filter((g) => zoneData.gate_ids?.includes(g.id))
      .every((g) => g.open === false)
  );

  const handleToggleZone = async () => {
    setActionLoading(true);
    try {
      await toggleZone(sessionToken, zoneId);
      await refreshZonePipes();
    } finally {
      setActionLoading(false);
    }
  };

  const removeZone = async () => {
    setActionLoading(true);
    try {
      await deleteZone({ id: zoneId }, sessionToken);
      Cookies.remove('active_zone_id');
      const newZones = await fetchData('allZones', sessionToken);
      setZones(newZones);
      navigate(-1);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="SingleZone">
      <div className='zone-section-1'>
        <div className='zone-command-section'>

          <h2 className="zone-title" onClick={() => setZoneNamePopup(true)}>
            Zone:{' '}
            {zoneNameLoading ? (
              <Spinner color="#3498db" backgroundColor="transparent" />
            ) : (
              zoneName
            )}
          </h2>

          <div className="toggle-buttons">
            <button
              className={`toggle-button on ${!allGatesClosed ? 'disabled' : ''}`}
              onClick={handleToggleZone}
              disabled={!allGatesClosed || actionLoading}
            >
              {actionLoading ? (
                <Spinner color="#ffffff" backgroundColor="#a5cde8" />
              ) : (
                'Turn On Zone'
              )}
            </button>

            <button
              className={`toggle-button off ${allGatesClosed ? 'disabled' : ''}`}
              onClick={handleToggleZone}
              disabled={allGatesClosed || actionLoading}
            >
              {actionLoading ? (
                <Spinner color="#ffffff" backgroundColor="#f2a5a5" />
              ) : (
                'Turn Off Zone'
              )}
            </button>
          </div>

          <button className="back-button" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>

        <div className="zone-water-sensor-section">
          {zoneWaterSensorsLoading ? (
            <Spinner color="#3498db" backgroundColor="transparent" />
          ) : zoneWaterSensors.length === 0 ? (
            <p className="no-ws-text">No Water Sensors Assigned</p>
          ) : (
            zoneWaterSensors
              .sort((a, b) => a.local_id - b.local_id)
              .map((sensor) => (
                <WaterSensorCard
                  key={sensor.id}
                  sensor={sensor}
                  onClick={(id) => {
                    console.log("Clicked WS:", id);
                  }}
                />
              ))
          )}
        </div>
      </div>

      <div className="pipe-content vertical-pipes">
        {pipeData.map((pipe) => {
          const totalGates = pipe.gates.length;

          const visualGates = pipe.gates
            .sort((a, b) => a.local_id - b.local_id)
            .map((g) => ({
              ...g,
              assigned: zoneData.gate_ids?.includes(g.id)
            }));

          return (
            <div key={pipe.id} className="pipe-section-card">
              <p className="pipe-column-id-label">
                Pipe {pipe.local_id} —{' '}
                {visualGates.filter((g) => g.assigned).length}/{totalGates} gates assigned
              </p>

              <div className="pipe-column-container multi-gate">
                {visualGates.map((gate) => {
                  let gateLabel = '';
                  if (gate.local_id === 0) gateLabel = 'Left';
                  else if (gate.local_id === 1) gateLabel = 'Right';
                  else gateLabel = `G${gate.id}`;

                  return (
                    <div
                      key={gate.id}
                      className={`gate-segment ${
                        gate.assigned
                          ? gate.open
                            ? 'open'
                            : 'closed'
                          : 'unassigned'
                      }`}
                      style={{ width: `${100 / totalGates}%` }}
                    >
                      <span>{gateLabel}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <button className="delete-button" onClick={removeZone}>
        {actionLoading ? (
          <Spinner color="#ffffff" backgroundColor="#f2a5a5" />
        ) : (
          'Delete Zone'
        )}
      </button>

      {zoneNamePopup && (
        <ZoneNamePopup
          selectedField={selectedField}
          closeZoneNamePopup={() => setZoneNamePopup(false)}
          selectedZone={{ id: zoneId, name: zoneName }}
        />
      )}
    </div>
  );
};

export default SingleZone;